import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import LegalPageLayout from '@/components/LegalPageLayout';
import { useAcceptanceLog } from '@/hooks/useAcceptanceLog';

const TermsOfUse = () => {
  const navigate = useNavigate();
  const { logAcceptance } = useAcceptanceLog();

  const handleAccept = () => {
    logAcceptance('termsOfUse', 'os Termos de Uso');
    navigate('/');
  };

  return (
    <>
      <Helmet>
        <title>Termos de Uso | Aceena</title>
        <meta name="description" content="Termos e Condições de Uso da plataforma Aceena. Conheça as regras e responsabilidades ao utilizar nossos serviços." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <LegalPageLayout
        title="Termos e Condições de Uso"
        lastUpdated="22 de abril de 2025"
        onAccept={handleAccept}
      >
        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">1. Introdução</h2>
          <p className="text-foreground/80 leading-relaxed">
            Bem-vindo à Aceena! Estes Termos e Condições de Uso ("Termos") regem o acesso e uso da plataforma Aceena ("Plataforma"), operada pela Aceena Tecnologia e Consultoria Automotiva Ltda. ("Aceena", "nós", "nosso"). Ao acessar ou utilizar a Plataforma, você concorda em cumprir e estar vinculado a estes Termos.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">2. Definições</h2>
          <ul className="list-disc pl-6 space-y-2 text-foreground/80">
            <li><strong>Usuário:</strong> Qualquer pessoa que acesse ou utilize a Plataforma.</li>
            <li><strong>Locadora:</strong> Empresas parceiras que oferecem serviços de assinatura de veículos através da Plataforma.</li>
            <li><strong>Serviços:</strong> Consultoria, curadoria e intermediação para contratação de assinaturas de veículos.</li>
            <li><strong>Selo Aceena:</strong> Certificação concedida a locadoras que atendem aos padrões de qualidade estabelecidos pela Aceena.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">3. A Plataforma Aceena</h2>
          <p className="text-foreground/80 leading-relaxed mb-4">
            A Aceena é uma plataforma de consultoria especializada em carro por assinatura. Nossos serviços incluem:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-foreground/80">
            <li>Análise técnica e imparcial de propostas de assinatura de veículos.</li>
            <li>Curadoria de locadoras parceiras com o Selo Aceena.</li>
            <li>Orientação sobre coberturas, serviços e cláusulas contratuais.</li>
            <li>Suporte contínuo durante toda a vigência do contrato de assinatura.</li>
          </ul>
          <p className="text-foreground/80 leading-relaxed mt-4">
            <strong>Importante:</strong> A Aceena atua como consultora e intermediária. O contrato de assinatura do veículo é firmado diretamente entre o Usuário e a Locadora escolhida.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">4. Cadastro e Conta</h2>
          <p className="text-foreground/80 leading-relaxed mb-4">
            Para utilizar alguns serviços da Plataforma, pode ser necessário criar uma conta. Ao criar uma conta, você concorda em:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-foreground/80">
            <li>Fornecer informações verdadeiras, precisas e completas.</li>
            <li>Manter suas informações de cadastro atualizadas.</li>
            <li>Manter a confidencialidade de suas credenciais de acesso.</li>
            <li>Notificar imediatamente a Aceena sobre qualquer uso não autorizado de sua conta.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">5. Uso da Plataforma</h2>
          <p className="text-foreground/80 leading-relaxed mb-4">Ao utilizar a Plataforma, você concorda em:</p>
          <ul className="list-disc pl-6 space-y-2 text-foreground/80">
            <li>Utilizar a Plataforma apenas para fins legais e de acordo com estes Termos.</li>
            <li>Não utilizar a Plataforma de forma que possa danificar, desabilitar ou sobrecarregar nossos sistemas.</li>
            <li>Não tentar acessar áreas restritas da Plataforma sem autorização.</li>
            <li>Não utilizar robôs, spiders ou outros dispositivos automatizados para acessar a Plataforma.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">6. Serviços de Consultoria</h2>
          <p className="text-foreground/80 leading-relaxed mb-4">
            Os serviços de consultoria da Aceena são gratuitos para o Usuário. Nossa remuneração é obtida através de parcerias com as Locadoras. Isso garante:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-foreground/80">
            <li>Imparcialidade nas recomendações, focando no melhor interesse do Usuário.</li>
            <li>Ausência de conflito de interesses na orientação prestada.</li>
            <li>Transparência em todas as etapas do processo de consultoria.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">7. Selo Aceena</h2>
          <p className="text-foreground/80 leading-relaxed mb-4">
            O Selo Aceena é concedido a locadoras que atendem aos nossos rigorosos critérios de qualidade, incluindo:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-foreground/80">
            <li>Transparência contratual e clareza nas condições oferecidas.</li>
            <li>Qualidade no atendimento ao cliente.</li>
            <li>Solidez financeira e reputação no mercado.</li>
            <li>Cobertura adequada de serviços e manutenção.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">8. Análise de Crédito</h2>
          <p className="text-foreground/80 leading-relaxed">
            Para a contratação de assinaturas de veículos, as Locadoras podem realizar análise de crédito do Usuário. Ao solicitar propostas através da Plataforma, você autoriza a realização desta análise. A Aceena não tem acesso aos resultados da análise de crédito, sendo esta uma relação direta entre o Usuário e a Locadora.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">9. Comunicações</h2>
          <p className="text-foreground/80 leading-relaxed">
            Ao utilizar a Plataforma, você concorda em receber comunicações da Aceena por e-mail, telefone ou WhatsApp, incluindo informações sobre serviços, atualizações e ofertas. Você pode optar por não receber comunicações promocionais a qualquer momento através das configurações de sua conta ou entrando em contato conosco.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">10. Propriedade Intelectual</h2>
          <p className="text-foreground/80 leading-relaxed">
            Todo o conteúdo da Plataforma, incluindo textos, gráficos, logotipos, ícones, imagens e software, é propriedade da Aceena ou de seus licenciadores e está protegido por leis de propriedade intelectual. Você não pode copiar, modificar, distribuir ou reproduzir qualquer conteúdo sem autorização prévia por escrito.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">11. Limitação de Responsabilidade</h2>
          <p className="text-foreground/80 leading-relaxed">
            A Aceena atua como intermediária e consultora. Não somos parte do contrato de assinatura firmado entre o Usuário e a Locadora. Portanto, não nos responsabilizamos por disputas, descumprimentos contratuais ou problemas relacionados à execução do contrato de assinatura pela Locadora.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">12. Indenização</h2>
          <p className="text-foreground/80 leading-relaxed">
            Você concorda em indenizar e isentar a Aceena, seus diretores, funcionários e parceiros de qualquer reclamação, perda ou dano decorrente de seu uso da Plataforma ou violação destes Termos.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">13. Alterações nos Termos</h2>
          <p className="text-foreground/80 leading-relaxed">
            A Aceena reserva-se o direito de modificar estes Termos a qualquer momento. Alterações significativas serão comunicadas através da Plataforma ou por e-mail. O uso continuado da Plataforma após as alterações constitui aceitação dos novos Termos.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">14. Lei Aplicável e Foro</h2>
          <p className="text-foreground/80 leading-relaxed">
            Estes Termos são regidos pelas leis da República Federativa do Brasil. Qualquer disputa decorrente destes Termos será submetida ao foro da Comarca de São Paulo, Estado de São Paulo, com exclusão de qualquer outro.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">15. Contato</h2>
          <p className="text-foreground/80 leading-relaxed">
            Para dúvidas ou esclarecimentos sobre estes Termos, entre em contato pelo e-mail{' '}
            <a href="mailto:contato@aceena.com.br" className="text-aceena-green hover:underline">
              contato@aceena.com.br
            </a>
            {' '}ou pelo telefone{' '}
            <a href="tel:+5511964774179" className="text-aceena-green hover:underline">
              (11) 96477-4179
            </a>.
          </p>
        </section>
      </LegalPageLayout>
    </>
  );
};

export default TermsOfUse;
